import { useState, useEffect, useRef } from 'react'
import { v4 as uuidv4 } from 'uuid'
import type { ClientProfile, ClientBranding, Settings, DemoModeState } from '@shared/types'
import { formatDashboardTitle } from '@tn/shared'
import { useApi } from '../../contexts/ApiContext'
import { useGenerationContext, computeProgress } from '../../contexts/GenerationContext'
import { useDropdownOptions } from '../../utils/useDropdownOptions'

interface Props {
  profile: Partial<ClientProfile> & Pick<ClientProfile, 'companyName' | 'functionScope' | 'ambitionLevel'>
  branding: ClientBranding
  mmsId?: string
  purpose?: string
  onBack: () => void
  onDone: (projectId: string) => void
}

export default function Step3_Generate({ profile, branding, mmsId, purpose, onBack, onDone }: Props) {
  const api = useApi()
  const { activeJobs, trackJob, cancelJob } = useGenerationContext()
  const { industries: industryChoices } = useDropdownOptions()
  const [settings, setSettings] = useState<Settings | null>(null)
  const [demoState, setDemoState] = useState<DemoModeState | null>(null)
  const [running, setRunning] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [projectId] = useState(() => uuidv4())
  const [currentJobId, setCurrentJobId] = useState<string | null>(null)
  const [confirmingCancel, setConfirmingCancel] = useState(false)
  const [placeholderMsgs, setPlaceholderMsgs] = useState<{ text: string; done: boolean }[]>([])
  const placeholderTimer1Ref = useRef<ReturnType<typeof setTimeout> | null>(null)
  const placeholderTimer2Ref = useRef<ReturnType<typeof setTimeout> | null>(null)
  const keepAliveRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const isMountedRef = useRef(true)
  // Latest onDone without re-running the job-watch effects when the parent re-renders.
  const onDoneRef = useRef(onDone)
  useEffect(() => { onDoneRef.current = onDone }, [onDone])

  useEffect(() => {
    isMountedRef.current = true
    api.getSettings().then((s) => { if (isMountedRef.current) setSettings(s) })
    api.getDemoState().then((d) => { if (isMountedRef.current) setDemoState(d) })
    return () => {
      isMountedRef.current = false
      if (placeholderTimer1Ref.current) clearTimeout(placeholderTimer1Ref.current)
      if (placeholderTimer2Ref.current) clearTimeout(placeholderTimer2Ref.current)
      if (keepAliveRef.current) clearInterval(keepAliveRef.current)
    }
  }, [api])

  // Keep the ESO session alive while this page is open (generation can take many minutes).
  useEffect(() => {
    if (api.platform !== 'web') return
    const id = setInterval(() => {
      window.dispatchEvent(new CustomEvent('tn:generation-keepalive'))
    }, 3 * 60 * 1000)
    return () => clearInterval(id)
  }, [api.platform])

  // Watch context for job completion
  useEffect(() => {
    if (!currentJobId) return
    const job = activeJobs.find((j) => j.jobId === currentJobId)
    if (!job) return
    if (job.status === 'succeeded' || job.status === 'failed') {
      if (placeholderTimer1Ref.current) { clearTimeout(placeholderTimer1Ref.current); placeholderTimer1Ref.current = null }
      if (placeholderTimer2Ref.current) { clearTimeout(placeholderTimer2Ref.current); placeholderTimer2Ref.current = null }
      if (keepAliveRef.current) { clearInterval(keepAliveRef.current); keepAliveRef.current = null }
      if (!isMountedRef.current) return
      if (job.status === 'succeeded') {
        onDoneRef.current(projectId)
      } else {
        setRunning(false)
        setError(job.error || 'Generation failed')
      }
    }
  }, [activeJobs, currentJobId, projectId])

  // Fallback: when the job poll endpoint is blocked (e.g. 403), poll the
  // project directly every 15 s. Navigate as soon as project.data exists —
  // the server only writes data after generation fully completes.
  useEffect(() => {
    if (!currentJobId) return
    const job = activeJobs.find((j) => j.jobId === currentJobId)
    if (!job || job.status !== 'running') return
    if (job.progress.length === 0) return

    const timer = setInterval(async () => {
      try {
        const project = await api.getProject(projectId)
        if (project?.data) {
          if (isMountedRef.current) onDoneRef.current(projectId)
        }
      } catch { /* ignore */ }
    }, 15_000)

    return () => clearInterval(timer)
  }, [api, activeJobs, currentJobId, projectId])

  const trackedJob = currentJobId ? activeJobs.find((j) => j.jobId === currentJobId) : null
  const contextProgress = trackedJob?.progress ?? []
  const { pct, label } = computeProgress(contextProgress)

  // Other outside-in jobs running in the background (not the one started on this page)
  const otherActiveJobs = activeJobs.filter(
    (j) => j.type === 'outside-in' && j.jobId !== currentJobId && (j.status === 'pending' || j.status === 'running')
  )

  const isDemo = demoState?.active ?? false
  const hasApiKey = isDemo || (settings
    ? settings.aiProvider === 'claude'
      ? settings.anthropicApiKey.length > 0
      : settings.aiProvider === 'azure-openai'
        ? settings.azureApiKey.length > 0 && settings.azureEndpoint.length > 0 && settings.azureDeployment.length > 0
        : settings.openaiApiKey.length > 0
    : false)

  const generate = async () => {
    if (!settings) return
    setRunning(true)
    setError(null)
    setPlaceholderMsgs([{ text: 'Starting generation…', done: false }])

    keepAliveRef.current = setInterval(() => {
      window.dispatchEvent(new CustomEvent('tn:generation-keepalive'))
    }, 270_000)

    placeholderTimer1Ref.current = setTimeout(() => {
      setPlaceholderMsgs([
        { text: 'Starting generation…', done: true },
        { text: 'Analyzing workforce structure…', done: false },
      ])
    }, 5 * 60 * 1000)

    placeholderTimer2Ref.current = setTimeout(() => {
      setPlaceholderMsgs([
        { text: 'Starting generation…', done: true },
        { text: 'Analyzing workforce structure…', done: true },
        { text: 'Generating role insights…', done: false },
      ])
    }, 15 * 60 * 1000)

    try {
      await api.saveProject({
        id: projectId,
        name: formatDashboardTitle(profile.companyName),
        client: profile.companyName,
        industry: profile.industry || '',
        mode: 'outside-in',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        branding,
        profile,
        mmsId: mmsId || undefined,
        purpose: purpose || undefined,
      })

      const apiKey =
        settings.aiProvider === 'claude' ? settings.anthropicApiKey
        : settings.aiProvider === 'azure-openai' ? settings.azureApiKey
        : settings.openaiApiKey

      const result = await api.generateOutsideIn({
        projectId,
        profile,
        branding,
        provider: settings.aiProvider,
        apiKey,
        industryChoices: profile.industry ? undefined : industryChoices,
      })

      trackJob(result.jobId, projectId, 'outside-in', profile.companyName, branding.primaryColor)
      setCurrentJobId(result.jobId)
    } catch (err) {
      if (placeholderTimer1Ref.current) { clearTimeout(placeholderTimer1Ref.current); placeholderTimer1Ref.current = null }
      if (placeholderTimer2Ref.current) { clearTimeout(placeholderTimer2Ref.current); placeholderTimer2Ref.current = null }
      if (keepAliveRef.current) { clearInterval(keepAliveRef.current); keepAliveRef.current = null }
      setError(err instanceof Error ? err.message : String(err))
      setRunning(false)
    }
  }

  if (!settings) return <div className="text-gray-400 text-sm">Loading settings…</div>

  return (
    <div className="max-w-2xl">
      <h2 className="text-xl font-semibold text-gray-900 mb-1">Generate Navigator</h2>
      <p className="text-sm text-gray-500 mb-6">
        Talent Navigator will generate task-level transformation analysis for each subfunction using the TSCV methodology.
      </p>

      {/* Summary */}
      <div className="bg-gray-50 rounded-xl p-4 mb-6 space-y-2 text-sm">
        <div className="flex justify-between">
          <span className="text-gray-500">Client</span>
          <span className="font-medium text-gray-900">{profile.companyName}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500">Industry</span>
          <span className={profile.industry ? 'font-medium text-gray-900' : 'text-gray-400 italic'}>
            {profile.industry || 'Claude will research'}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500">Geography</span>
          <span className={profile.geography ? 'font-medium text-gray-900' : 'text-gray-400 italic'}>
            {profile.geography || 'Claude will research'}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500">Headcount</span>
          <span className={profile.headcountMin !== undefined ? 'font-medium text-gray-900' : 'text-gray-400 italic'}>
            {profile.headcountMin !== undefined && profile.headcountMax !== undefined
              ? `${profile.headcountMin.toLocaleString()}–${profile.headcountMax.toLocaleString()}`
              : 'Claude will research'}
          </span>
        </div>
        {profile.targetHeadcount !== undefined && (
          <div className="flex justify-between">
            <span className="text-gray-500">In-Scope Headcount</span>
            <span className="font-medium text-gray-900">{profile.targetHeadcount.toLocaleString()}</span>
          </div>
        )}
        <div className="flex justify-between">
          <span className="text-gray-500">Functions</span>
          <span className="font-medium text-gray-900">
            {profile.functionScope.length === 0 ? 'Full Enterprise (AI identifies all functions)' : profile.functionScope.join(', ')}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500">AI Provider</span>
          <span className="font-medium text-gray-900">{settings.aiProvider === 'claude' ? 'Claude (Anthropic)' : settings.aiProvider === 'azure-openai' ? 'Azure OpenAI' : 'GPT-4o (OpenAI)'}</span>
        </div>
      </div>

      {!hasApiKey && !isDemo && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
          No API key configured for {settings.aiProvider}. Go to Settings to add one.
        </div>
      )}

      {isDemo && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
          Demo Mode — Navigator will be generated from pre-built sample data.
        </div>
      )}

      {/* Notice: other outside-in jobs running in background */}
      {otherActiveJobs.length > 0 && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
          {otherActiveJobs.length === 1
            ? <>Generation in progress for <span className="font-medium">{otherActiveJobs[0].clientName}</span> — monitor it from the Home page.</>
            : <>{otherActiveJobs.length} generations in progress — monitor them from the Home page.</>
          }
        </div>
      )}

      {/* Progress */}
      {running && (
        <div className="mb-6 bg-white border border-gray-100 rounded-xl p-4">
          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Generation Progress</div>

          {/* Progress bar */}
          {contextProgress.length > 0 && (
            <div className="mb-4">
              <div className="flex items-center justify-between text-xs text-gray-500 mb-1.5">
                <span>{label}</span>
                <span className="font-medium text-purple-600">{pct}%</span>
              </div>
              <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-1000"
                  style={{ width: `${pct}%`, background: 'linear-gradient(90deg, #A100FF, #7900B8)' }}
                />
              </div>
            </div>
          )}

          <div className="space-y-1.5 max-h-48 overflow-y-auto">
            {contextProgress.length === 0 ? (
              placeholderMsgs.map((msg) => (
                <div key={msg.text} className="flex items-center gap-2.5 text-sm text-gray-400">
                  {msg.done
                    ? <span className="text-green-500">✓</span>
                    : <span className="animate-pulse-slow text-purple-400">⟳</span>
                  }
                  {msg.text}
                </div>
              ))
            ) : (
              contextProgress.map((item) => (
                <div key={item.subfunctionName} className="flex items-center gap-2.5 text-sm">
                  {item.status === 'done' && <span className="text-green-500">✓</span>}
                  {item.status === 'error' && <span className="text-red-400">✗</span>}
                  {item.status === 'generating' && <span className="animate-pulse-slow text-purple-500">⟳</span>}
                  {item.status !== 'done' && item.status !== 'error' && item.status !== 'generating' && <span className="text-gray-300">○</span>}
                  <span className={item.status === 'error' ? 'text-red-500' : 'text-gray-700'}>
                    {item.subfunctionName}
                  </span>
                  {item.error && (
                    <span className="text-xs text-red-400 ml-auto">{item.error.slice(0, 50)}</span>
                  )}
                </div>
              ))
            )}
          </div>

          <div className="mt-3 pt-3 border-t border-gray-50 text-xs text-gray-400 flex items-center justify-between gap-1.5">
            <div className="flex items-center gap-1.5">
              <span>💡</span>
              <span>You can safely navigate away — generation continues in the background.</span>
            </div>
            {confirmingCancel ? (
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className="text-gray-500">Stop generation?</span>
                <button
                  onClick={() => { if (currentJobId) void cancelJob(currentJobId) }}
                  className="text-red-500 font-medium underline"
                >
                  Yes, cancel
                </button>
                <button
                  onClick={() => setConfirmingCancel(false)}
                  className="text-gray-400 underline"
                >
                  Keep going
                </button>
              </div>
            ) : (
              <button
                onClick={() => setConfirmingCancel(true)}
                className="text-red-400 hover:text-red-600 underline flex-shrink-0"
              >
                Cancel
              </button>
            )}
          </div>
        </div>
      )}

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          ✗ {error}
        </div>
      )}

      <div className="flex gap-3">
        <button
          onClick={onBack}
          disabled={running}
          className="py-3 px-6 rounded-xl border-2 border-gray-200 text-sm font-semibold text-gray-600 hover:border-gray-300 disabled:opacity-40 transition-all"
        >
          ← Back
        </button>
        <button
          onClick={generate}
          disabled={running || !hasApiKey}
          className="flex-1 py-3 px-8 rounded-xl text-white text-sm font-semibold disabled:opacity-40 transition-all"
          style={{ background: 'linear-gradient(135deg, #A100FF, #7900B8)' }}
        >
          {running ? (
            <span className="flex items-center justify-center gap-2">
              <span className="animate-pulse-slow">⟳</span>
              Generating…
            </span>
          ) : (
            '🚀 Generate Navigator'
          )}
        </button>
      </div>
    </div>
  )
}
